import { useState, useEffect } from 'react';
import { 
  PRIZES, 
  LEGACY_PRIZES, 
  TOTAL_PRIZE_POOL, 
  REWARD_SCALING, 
  PRIZE_DISTRIBUTION, 
  calculatePrizePool 
} from './leaderboardConfig';

// Re-export for accessibility
export { PRIZES, LEGACY_PRIZES, REWARD_SCALING, PRIZE_DISTRIBUTION, calculatePrizePool };

// API endpoint for the backend server
const API_BASE_URL = '/api';  // Use relative URL for proxying through Vite

// Define types
export interface LeaderboardData {
  rank: number;
  username: string;
  wagered: number;
  prize: number;
}

export interface PreviousLeaderboardEntry {
  rank: number;
  name: string;
  uid: string;
  wager: number;
  prize: number;
}

export interface PreviousLeaderboardResponse {
  success: boolean;
  data: PreviousLeaderboardEntry[];
  period: {
    start: string;
    end: string;
  };
}

// Function to fetch previous leaderboard data
export const fetchPreviousLeaderboard = async (): Promise<PreviousLeaderboardResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/previous-leaderboard`);
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching previous leaderboard:', error);
    throw error;
  }
};

// Fallback data in case API is unavailable
export const fallbackData: LeaderboardData[] = [
  { rank: 1, username: "-", wagered: 0, prize: PRIZES[1] },
  { rank: 2, username: "-", wagered: 0, prize: PRIZES[2] },
  { rank: 3, username: "-", wagered: 0, prize: PRIZES[3] },
  { rank: 4, username: "-", wagered: 0, prize: PRIZES[4] },
  { rank: 5, username: "-", wagered: 0, prize: PRIZES[5] },
  { rank: 6, username: "-", wagered: 0, prize: PRIZES[6] },
  { rank: 7, username: "-", wagered: 0, prize: PRIZES[7] },
  { rank: 8, username: "-", wagered: 0, prize: PRIZES[8] },
  { rank: 9, username: "-", wagered: 0, prize: PRIZES[9] },
  { rank: 10, username: "-", wagered: 0, prize: PRIZES[10] }
];

// Define hook return type
interface LeaderboardHookReturn {
  leaderboardData: LeaderboardData[];
  error: string | null;
  loading: boolean;
  lastUpdated: Date | null;
  cycleStartDate: Date;
  cycleEndDate: Date;
  totalPrizePool: number;
  totalWagered: number;
  prizes: Record<number, number>;
}

// Main hook to access leaderboard data
export const useLeaderboard = (): LeaderboardHookReturn => {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData[]>(fallbackData);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [cycleStartDate, setCycleStartDate] = useState<Date>(new Date());
  const [cycleEndDate, setCycleEndDate] = useState<Date>(new Date());
  const [totalWagered, setTotalWagered] = useState<number>(0);
  const [dynamicPrizePool, setDynamicPrizePool] = useState<number>(0);
  const [dynamicPrizes, setDynamicPrizes] = useState<Record<number, number>>({});

  // Calculate dynamic prizes based on total wagered amount
  const calculateDynamicPrizes = (totalWager: number) => {
    const prizePool = calculatePrizePool(totalWager);
    setDynamicPrizePool(prizePool);
    
    // Calculate individual prizes based on distribution percentages
    const calculatedPrizes: Record<number, number> = {};
    for (let rank = 1; rank <= 10; rank++) {
      calculatedPrizes[rank] = parseFloat((prizePool * PRIZE_DISTRIBUTION[rank as keyof typeof PRIZE_DISTRIBUTION]).toFixed(2));
    }
    setDynamicPrizes(calculatedPrizes);
    
    return calculatedPrizes;
  };

  // Fetch data from the backend API
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/leaderboard`);

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (data.success && Array.isArray(data.data)) {
          // Calculate total wagered amount
          let totalWageredAmount = 0;
          if (data.data.length > 0) {
            // Sum all wagers or use the highest wager as an approximation
            totalWageredAmount = data.data.reduce((sum: number, item: any) => sum + item.wager, 0);
          }
          setTotalWagered(totalWageredAmount);
          
          // Use the prizes directly from the API response
          // Map the response data to our LeaderboardData structure with prizes from API
          const formattedData = data.data.map((item: any) => ({
            rank: item.rank,
            username: item.name,
            wagered: item.wager,
            prize: item.prize || 0 // Use prize value from API response
          }));
          
          // Set dynamic prizes for frontend display (used by the prize displays)
          const prizeMap: Record<number, number> = {};
          data.data.forEach((item: any) => {
            prizeMap[item.rank] = item.prize || 0;
          });
          setDynamicPrizes(prizeMap);

          setLeaderboardData(formattedData);
          setLastUpdated(new Date());
          setCycleStartDate(new Date(data.cycleStart));
          setCycleEndDate(new Date(data.cycleEnd));
          setDynamicPrizePool(data.totalPrizePool || TOTAL_PRIZE_POOL);
          setError(null);
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        setError('Failed to fetch leaderboard data. Please try again later.');
        setLeaderboardData(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboardData();

    // Refresh data every 5 minutes
    const intervalId = setInterval(fetchLeaderboardData, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  return {
    leaderboardData,
    error,
    loading,
    lastUpdated,
    cycleStartDate,
    cycleEndDate,
    totalPrizePool: dynamicPrizePool,
    totalWagered,
    prizes: dynamicPrizes
  };
};